import { colorSystem } from '@luminovo/design-system';
import { Box } from '@mui/material';
import * as React from 'react';

import NavbarRouter from '@/app/NavbarRouter';
import { ErrorBoundaryClearedOnNavigation } from '@/shared/ui/errors/ErrorBoundaryClearedOnNavigation';

/** The navbar above the page; outside `AuthenticatedLayout`, so route tests that stub the providers still see it. */
export function AppFrame({ children }: { children: React.ReactNode }): JSX.Element {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flexShrink: 0 }}>
                {/* Outlives the pages, so it clears on navigation: a failed navbar returns with the next page. */}
                <ErrorBoundaryClearedOnNavigation fallback={NavbarErrorFallback}>
                    {/* The navbar stays mounted, so suspending on data a navigation changes would flash this. */}
                    <React.Suspense fallback={<NavbarSkeleton />}>
                        <NavbarRouter />
                    </React.Suspense>
                </ErrorBoundaryClearedOnNavigation>
            </Box>
            <Box sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
        </Box>
    );
}

const navbarPlaceholderSx = { height: '48px', minHeight: '48px', background: colorSystem.neutral[9] };

function NavbarSkeleton(): JSX.Element {
    return <Box data-testid="navbar-skeleton" sx={navbarPlaceholderSx} />;
}

/** A failed navbar, until the next navigation clears the error; looks like the skeleton. */
function NavbarErrorFallback(): JSX.Element {
    return <Box data-testid="navbar-error" sx={navbarPlaceholderSx} />;
}
